// src/hooks/useSessionBootstrap.js
//
// Decides how the app starts:
//   loading -> consent (first visit) -> ready
//   loading -> choose (list of this browser's journeys to continue / "Start a new journey") -> ready
//   ?resume=<token> (resume link) -> consent if needed -> ready on the resumed session
// The session cookie is only created after the user accepted the cookie notice.

import { useCallback, useEffect, useRef, useState } from "react";
import { getSession, getSessionStatus, listJourneys, startSession, switchJourney } from "../api";
import { friendlyError } from "../utils/errors";

const CONSENT_KEY = "t4r_ljd_cookie_consent_v1";

export function hasStoredConsent() {
  try {
    return window.localStorage.getItem(CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
}

function storeConsent() {
  try {
    window.localStorage.setItem(CONSENT_KEY, "accepted");
  } catch {
    /* ignore */
  }
}

function readUrl() {
  const params = new URLSearchParams(window.location.search);
  const step = parseInt(params.get("step") || "", 10);
  return { resume: params.get("resume"), step: Number.isFinite(step) ? step : null };
}

function cleanUrl() {
  try {
    // Never keep the resume token in the address bar / history.
    window.history.replaceState(null, "", window.location.pathname);
  } catch {
    /* ignore */
  }
}

async function fetchJourneys() {
  try {
    return (await listJourneys()).journeys || [];
  } catch {
    return [];
  }
}

/** A link may ask for a step (e.g. ?step=4), but never further than the first step still to confirm. */
const reachableStep = (requested, summary) => {
  const current = summary?.session?.current_step || 0;
  return requested ? Math.min(requested, current || requested) : current;
};

export function useSessionBootstrap({ enabled = true } = {}) {
  const [phase, setPhase] = useState("loading"); // loading | consent | declined | choose | ready | error
  const [summary, setSummary] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [initialStep, setInitialStep] = useState(0);
  const [error, setError] = useState(null);
  const url = useRef(readUrl());

  const finish = useCallback((s, step) => {
    setSummary(s);
    setInitialStep(step ?? 0);
    setPhase("ready");
    cleanUrl();
  }, []);

  const create = useCallback(async () => {
    const { resume, step } = url.current;
    try {
      const s = await startSession({ resumeToken: resume });
      finish(s, resume ? reachableStep(step, s) : 0);
    } catch (err) {
      if (err.code === "invalid_resume_link") {
        url.current = { resume: null, step: null };
        cleanUrl();
        setError("This resume link is invalid or has expired. You can start a new journey.");
        setPhase("choose");
        return;
      }
      setError(friendlyError(err, "Could not start a session."));
      setPhase("error");
    }
  }, [finish]);

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      const { resume } = url.current;
      let existing = null;
      try {
        const { active } = await getSessionStatus();
        existing = active ? await getSession() : null;
      } catch (err) {
        if (err.code !== "no_session") {
          setError(friendlyError(err, "Could not reach the server."));
          setPhase("error");
          return;
        }
      }
      if (resume) {
        // A cookie already exists -> consent was given before.
        if (existing || hasStoredConsent()) return create();
        return setPhase("consent");
      }
      // Any journey with something filled in (this one or earlier ones in this browser): let the user pick
      const list = await fetchJourneys();
      if (list.length) {
        setSummary(existing);
        setJourneys(list);
        return setPhase("choose");
      }
      if (existing) return finish(existing, url.current.step ? reachableStep(url.current.step, existing) : 0);
      if (hasStoredConsent()) return create();
      setPhase("consent");
    })();
  }, [enabled, create, finish]);

  const accept = useCallback(() => {
    storeConsent();
    setPhase("loading");
    create();
  }, [create]);

  const decline = useCallback(() => setPhase("declined"), []);
  const reviewConsent = useCallback(() => setPhase("consent"), []);

  const continueSession = useCallback(async (ref) => {
    const picked = journeys.find((j) => j.ref === ref);
    if (picked?.current && summary) {
      return finish(summary, reachableStep(url.current.step, summary));
    }
    setPhase("loading");
    try {
      const s = await switchJourney(ref);
      finish(s, s.session?.current_step || 0);
    } catch (err) {
      setError(friendlyError(err, "This journey is no longer available."));
      setJourneys(await fetchJourneys());
      setPhase("choose");
    }
  }, [finish, journeys, summary]);

  const startNew = useCallback(async () => {
    url.current = { resume: null, step: null };
    setError(null);
    setPhase("loading");
    await create();
  }, [create]);

  return { phase, summary, journeys, initialStep, error, accept, decline, reviewConsent, continueSession, startNew };
}
